import { ArrowLeft, Atom } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { BondingMode } from './IonsBondingBondingMode';
import { IonsMode } from './IonsBondingIonsMode';
import { SeniorMode } from './IonsBondingSeniorMode';
import {
  IONS_BONDING_DEFAULT_MODES,
  IONS_BONDING_MODES,
  IONS_BONDING_PROVENANCE,
  IONS_BONDING_TOOL_IDENTITY,
  type IonsBondingMode,
} from './ionsBonding';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';

const MODE_COPY: Record<IonsBondingMode, { tab: string; level: string }> = {
  ions: { tab: 'Ions & formulas', level: 'Year 10' },
  bonding: { tab: 'Bonding & properties', level: 'Year 10' },
  senior: { tab: 'Lewis & VSEPR', level: 'Year 11–12' },
};

export function IonsBondingLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<IonsBondingMode>(() => modeForYear(IONS_BONDING_DEFAULT_MODES, year));

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'ions-bonding', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-coral p-6 shadow-card-soft lg:p-8"
        data-tool-key={IONS_BONDING_TOOL_IDENTITY.toolKey}
        data-tool-version={IONS_BONDING_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Atom className="h-4 w-4" aria-hidden="true" /> Structure and bonding
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Ions &amp; Bonding Lab</h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Follow electrons from shells to ions, shared pairs, giant structures and molecular shapes.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year 10–12 · three modes
          </div>
        </div>

        <div className="mt-6 grid max-w-2xl grid-cols-1 gap-1 rounded-3xl bg-white/75 p-1 sm:grid-cols-3 sm:rounded-full" role="group" aria-label="Choose mode">
          {IONS_BONDING_MODES.map(({ mode: option }) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
              <span className="sr-only"> ({MODE_COPY[option].level})</span>
            </button>
          ))}
        </div>
      </header>

      <div className="my-6 flex justify-end">
        <span className="rounded-full bg-wash-sunshine px-4 py-2 text-xs font-extrabold text-ink-soft">
          {MODE_COPY[mode].level} · Explore only · not mastery evidence
        </span>
      </div>

      {mode === 'ions' ? <IonsMode /> : null}
      {mode === 'bonding' ? <BondingMode /> : null}
      {mode === 'senior' ? <SeniorMode /> : null}

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        Model boundary: the shell rule (2,8,8,2) covers Z = 1–20 only; transition-metal ions are reviewed
        reference data, not predictions. Dot-and-cross and Lewis diagrams show electron counts, not real
        positions; VSEPR gives approximate shapes. Diagrams are generated from the model. {IONS_BONDING_PROVENANCE.conditions}{' '}
        Sources: {IONS_BONDING_PROVENANCE.sources.join(' · ')}. Cases are pending teaching review. This tool does not
        save an attempt, record mastery or claim alignment to a state syllabus.
      </footer>
    </StudyShell>
  );
}
